import type { VoidFunctionComponent } from 'react';

import List from '../../../../../../../generic/List';
import type { ProjectsFilter } from '../../../../../../../../../shared/projectsV2/filters';
import type { Project } from '../../../../../../../../../shared/projectsV2/response';
import type { AddedProject } from '../../../../../../../../shared/branches/types';
import type { SelectOption } from '../../../../../../../../types';
import type { TeamSelectOption } from '../../../../../../../shared/teams/hooks/useTeamsSelectOptions';
import type { UseSelectProjects } from '../../../../hooks/useSelectProjects';
import type { AddedProjectIndex, ProjectSelection } from '../../../types';

import ProjectsTableBody from './ProjectsTableBody/ProjectsTableBody';
import ProjectsTableHeader from './ProjectsTableHeader/ProjectsTableHeader';

type Props = {
  projects: Project[];
  areProjectsLoading: boolean;
  projectErrors: string[] | null;
  totalProjectCount: number;
  addedProjects: AddedProjectIndex;
  selectProjects: UseSelectProjects;
  pageCount: number;
  filters: ProjectsFilter;
  onUpdateAddedProject: (project: AddedProject) => void;
  onAddProjects: (projects: Project[]) => void;
  onRemoveProjects: (projects: Project[]) => void;
  onAddAllProjects: () => void;
  onRemoveAllProjects: () => void;
  onSelectLatestRevisionForSelectedProjects: (selectedProjects: ProjectSelection) => void;
  onLabelClick: (selected: SelectOption[]) => void;
  selectedLabels: SelectOption[];
  onTeamClick: (selected: TeamSelectOption[]) => void;
  selectedTeams: TeamSelectOption[];
};

/**
 * The projects table is a paginated table which displays a list of projects.
 * It allows the user to select projects to add to a release group, as well
 * as search and sort the projects.
 */
const ProjectsTable: VoidFunctionComponent<Props> = ({
  projects,
  areProjectsLoading,
  projectErrors,
  addedProjects,
  selectProjects,
  totalProjectCount,
  pageCount,
  filters,
  onUpdateAddedProject,
  onAddProjects,
  onRemoveProjects,
  onAddAllProjects,
  onRemoveAllProjects,
  onSelectLatestRevisionForSelectedProjects,
  onLabelClick,
  selectedLabels,
  onTeamClick,
  selectedTeams,
}) => {
  const {
    selectedProjects,
    isProjectSelected,
    areAllProjectsOnPageSelected,
    areAllProjectsSelected,
    areSomeProjectsSelected,
    isSelectAllDisabled,
    onToggleIsProjectSelected,
    onToggleSelectAllProjects,
    onSelectAllAcrossAllPages,
    onClearSelectAll,
  } = selectProjects;

  const onAddSelectedProjects = () =>
    areAllProjectsSelected
      ? onAddAllProjects()
      : onAddProjects(projects.filter((project) => isProjectSelected(project.id)));
  const onRemoveSelectedProjects = () =>
    areAllProjectsSelected
      ? onRemoveAllProjects()
      : onRemoveProjects(projects.filter((project) => isProjectSelected(project.id)));
  const onSetRevisionToLatest = () => onSelectLatestRevisionForSelectedProjects(selectedProjects);

  return (
    <List>
      <List.Header hasCheckbox>
        <ProjectsTableHeader
          areProjectsLoading={areProjectsLoading}
          totalProjectCount={totalProjectCount}
          addedProjectsCount={Object.keys(addedProjects).length}
          areAllProjectsSelected={areAllProjectsOnPageSelected || areAllProjectsSelected}
          areSomeProjectsSelected={areSomeProjectsSelected}
          isSelectAllDisabled={isSelectAllDisabled}
          onToggleSelectAllProjects={onToggleSelectAllProjects}
          onAddSelectedProjects={onAddSelectedProjects}
          onRemoveSelectedProjects={onRemoveSelectedProjects}
          onSetRevisionToLatest={onSetRevisionToLatest}
        />
      </List.Header>
      <List.Body>
        <ProjectsTableBody
          areAllProjectsOnPageSelected={areAllProjectsOnPageSelected}
          areAllProjectsSelected={areAllProjectsSelected}
          totalProjectCount={totalProjectCount}
          onSelectAllAcrossAllPages={onSelectAllAcrossAllPages}
          onClearSelectAll={onClearSelectAll}
          areProjectsLoading={areProjectsLoading}
          projects={projects}
          projectErrors={projectErrors}
          addedProjects={addedProjects}
          pageCount={pageCount}
          filters={filters}
          onToggleIsProjectSelected={onToggleIsProjectSelected}
          isProjectSelected={isProjectSelected}
          onUpdateAddedProject={onUpdateAddedProject}
          onAddProjects={onAddProjects}
          onRemoveProjects={onRemoveProjects}
          onLabelClick={onLabelClick}
          selectedLabels={selectedLabels}
          onTeamClick={onTeamClick}
          selectedTeams={selectedTeams}
        />
      </List.Body>
    </List>
  );
};

export default ProjectsTable;
